/**
 * MessageList Component
 * Displays conversation messages with proper formatting
 */

import { useMemo } from 'react';
import { MessageItem } from './MessageItem';
import { CompactionMarker } from './CompactionMarker';
// Leaf import, not the projects barrel — see the note in Sidebar.tsx.
import { ProjectThreadPrimer } from '@modules/projects/components/ProjectThreadPrimer';
import { useLanguage } from '@/hooks/useLanguage';
import type { TextureColor } from '@/brand/textures';
import type { Message, ThinkingStep } from '@/types/models';
import { deriveModelSwitches } from '../utils/modelSwitches';
import { useAuth } from '@/hooks/useAuth';
import { firstOwnUses } from '@modules/connector_library/components/PersonalConnectorNotice';
import type { MessageFeedback } from './MessageRating';
import './MessageList.css';

/** One stable empty list, so a message with no reasons never busts MessageItem's memo. */
const NO_REASONS: string[] = [];

// Model display names for cleaner UI
const MODEL_DISPLAY_NAMES: Record<string, string> = {
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-opus-4-6': 'Claude Opus 4.6',
  'claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'gpt-5.2': 'GPT-5.2',
  'gpt-5': 'GPT-5',
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5-nano': 'GPT-5 Nano',
  'gpt-4o-search-preview': 'GPT-4o Search',
  'gpt-4o-mini-search-preview': 'GPT-4o Mini Search',
  'gpt-5.4-mini': 'GPT-5.4 Mini',
  'gemini-3.1-pro-preview': 'Gemini 3.1 Pro',
  'gemini-3-flash-preview': 'Gemini 3 Flash',
  'gemini-3.1-flash-lite': 'Gemini 3.1 Flash Lite',
};

// Get display name for a model ref. Live-streamed messages carry the FAMILY
// key from the picker ('gpt-mini'), persisted messages carry the concrete id
// from metadata ('gpt-5.4-mini') — the caller-provided catalog resolver (see
// `resolveModelName` prop) handles both; this static map is only the offline
// fallback for legacy concrete ids.
const getModelDisplayName = (
  modelId: string | undefined,
  resolveModelName?: (ref: string) => string | undefined,
): string => {
  if (!modelId) return '';
  // Strip 'models/' prefix if present (Gemini models sometimes have this)
  const cleanId = modelId.replace(/^models\//, '');
  return resolveModelName?.(cleanId) || MODEL_DISPLAY_NAMES[cleanId] || cleanId;
};

interface MessageListProps {
  messages: Message[];
  loading?: boolean;
  streaming?: boolean;  // Keep for compatibility but not used for skill badges
  onEditMessage?: (message: Message) => void;
  onBranch?: (message: Message, clickEvent?: { x: number; y: number }) => void;
  /** Open the agent chooser to hand the conversation off from this message. */
  onBranchToAgent?: (message: Message) => void;
  /** Jump to the conversation on the other side of a handoff card. */
  onHandoffTeleport?: (conversationId: string, messageId?: string) => void;
  /** Open the linked conversation beside this one instead of navigating. */
  onHandoffSplit?: (conversationId: string) => void;
  onScrollToQuote?: (quotedText: string) => void;
  onArtifactOpen?: (fileId: string) => void;
  /** Continue an answer the output cap paused — offered on the conversation's
   *  LAST message only, and only while nothing streams. Absent when the
   *  viewer cannot write to the conversation. */
  onContinue?: (message: Message) => Promise<void>;
  branchPointMessageId?: string;
  activeSkills?: string[];  // Skills currently being used
  thinkingSteps?: ThinkingStep[];  // Steps currently streaming
  /** Active ChatGPT-style indicator phase for the streaming message. */
  thinkingPhase?: 'uploading' | 'opening' | 'preparing' | 'working' | 'thinking';
  /** Filename surfaced during the `reading` phase (original user-facing name). */
  thinkingFilename?: string;
  /** Override label for the thinking indicator (live progress sentences). */
  thinkingText?: string;
  /** Epoch ms when the current status started — drives the elapsed counter. */
  thinkingSince?: number;
  revealThinking?: boolean;  // Show thinking banner
  showSkillLabels?: boolean;  // Show skill badges (default: true)
  conversationId?: string;
  conversationTitle?: string;
  availableSkills?: Array<{ skill_id: string; skill_name: string }>;
  skillFeedbackMap?: Record<string, { expected_skills: Array<{ skill_id: string; skill_name: string }>; rating?: 'like' | 'dislike' | null; reasons?: string[]; feedback_description?: string | null }>;
  onSkillFeedbackChange?: (messageId: string, skills: Array<{ skill_id: string; skill_name: string }>) => void;
  onRatingChange?: (messageId: string, feedback: MessageFeedback) => void;
  botColor?: TextureColor | null;  // Agent's chosen color — drives coin avatar
  botName?: string;
  botVariant?: number | null;
  /** When true, every message's fork button renders disabled (cap reached). */
  branchDisabled?: boolean;
  /** Tooltip text shown on the disabled fork button. */
  branchDisabledReason?: string;
  /**
   * Catalog-driven display-name resolver for model refs (family keys AND
   * concrete ids). Supplied by ConversationPanel from its loaded model
   * catalog so dividers show "GPT Mini", not the raw 'gpt-mini' key.
   */
  resolveModelName?: (ref: string) => string | undefined;
  /**
   * Canonical identity for a model ref — the catalogue entry's id, which is
   * the same for a family key and for every concrete version inside it. The
   * switch divider compares this, never the raw ref.
   */
  resolveModelIdentity?: (ref: string) => string | undefined;
  /** Label each user message with its author. On for shared-project threads,
   *  which are written by several people. */
  showSenderIdentity?: boolean;
  /** Set for a thread inside a shared project. Replaces the one-line empty
   *  state with what the agent has already been given — see
   *  ProjectThreadPrimer. */
  projectId?: string | null;
  /** A review of someone else's conversation (Insights → Feedback): see
   *  MessageItem's `readOnly`. */
  readOnly?: boolean;
  /** For a review: the likes and dislikes people left, by message id —
   *  each rated answer carries a small marker. */
  feedbackByMessage?: Record<string, { likes: number; dislikes: number }>;
  /** When given, an attachment card opens through it, not the built-in preview. */
  onAttachmentOpen?: (attachmentId: string) => void;
}

export function MessageList({ messages, loading = false, streaming = false, onEditMessage, onBranch, onBranchToAgent, onHandoffTeleport, onHandoffSplit, onScrollToQuote, onArtifactOpen, onContinue, branchPointMessageId, activeSkills = [], thinkingSteps = [], thinkingPhase = 'thinking', thinkingFilename, thinkingText, thinkingSince, revealThinking, showSkillLabels, conversationId, conversationTitle, availableSkills = [], skillFeedbackMap = {}, onSkillFeedbackChange, onRatingChange, botColor, botName, botVariant, branchDisabled = false, branchDisabledReason, resolveModelName, resolveModelIdentity, showSenderIdentity = false, projectId, readOnly = false, feedbackByMessage, onAttachmentOpen }: MessageListProps) {
  const { t } = useLanguage();

  // Ensure messages is always an array.
  // Keep a message if it has content, OR it's an assistant message (streaming
  // placeholders), OR it's a user message carrying file attachments — a
  // files-only message has empty content but must still render.
  const validMessages = Array.isArray(messages) ? messages.filter(m =>
    m && m.message_id && (
      m.content || m.role === 'assistant' || (m.attachments?.length ?? 0) > 0
    )
  ) : [];

  // Find the last user message ID (only this one can be edited)
  // A shared (project) thread: where the viewer's own account to a service
  // was first used, the answer says, once, that the thread's members see it.
  const { user } = useAuth();
  const sharedNoticeAt = useMemo(
    () => (projectId ? firstOwnUses(messages, user?.user_id) : new Set<string>()),
    [projectId, messages, user?.user_id]);
  const lastUserMessageId = useMemo(() => {
    for (let i = validMessages.length - 1; i >= 0; i--) {
      if (validMessages[i].role === 'user') {
        return validMessages[i].message_id;
      }
    }
    return null;
  }, [validMessages]);

  // A paused answer can be continued only while it is the conversation's
  // latest message: once anything follows it, the user has moved on.
  const continuableMessageId = useMemo(() => {
    const last = validMessages[validMessages.length - 1];
    return last && last.role === 'assistant' && last.answer_cut_off
      && !last.message_id.startsWith('temp-') && (last.content || '').trim()
      ? last.message_id : null;
  }, [validMessages]);

  // Find the last assistant message (for passing skills to streaming message)
  const lastAssistantMessageId = useMemo(() => {
    for (let i = validMessages.length - 1; i >= 0; i--) {
      if (validMessages[i].role === 'assistant') {
        return validMessages[i].message_id;
      }
    }
    return null;
  }, [validMessages]);

  // Find mode switches — same pattern as modelSwitches but using mode_label.
  // The divider appears BEFORE the assistant message generated under the new mode.
  // Mode label is persisted on each assistant message via message_metadata.mode_label
  // (set by the backend when mode_slider_config is enabled), so this derivation
  // also works after a page reload.
  const modeSwitches = useMemo(() => {
    const switches: Record<string, { from: string; to: string }> = {};
    let lastMode: string | undefined;
    for (const msg of validMessages) {
      if (msg.role === 'assistant' && msg.mode_label) {
        if (lastMode && lastMode !== msg.mode_label) {
          switches[msg.message_id] = { from: lastMode, to: msg.mode_label };
        }
        lastMode = msg.mode_label;
      }
    }
    return switches;
  }, [validMessages]);

  // Find model switches - returns map of message_id -> switch info
  // The indicator should appear BEFORE the assistant message that uses the new
  // model. The comparison is on catalogue IDENTITY, not on the raw ref: a live
  // message carries the family key and a reloaded one the concrete id, so a
  // raw compare drew a "Sonnet → Sonnet" divider on every turn. See
  // ../utils/modelSwitches.ts.
  const modelSwitches = useMemo(
    () => deriveModelSwitches(validMessages, {
      resolveIdentity: resolveModelIdentity,
      resolveName: (ref) => getModelDisplayName(ref, resolveModelName),
    }),
    [validMessages, resolveModelName, resolveModelIdentity],
  );

  if (loading && validMessages.length === 0) {
    return (
      <div className="chat-messages">
        <div className="empty-state">
          <p>{t('common.loadingMessages')}</p>
        </div>
      </div>
    );
  }

  if (validMessages.length === 0) {
    return (
      <div className="chat-messages">
        <div className="empty-conversation-state">
          {projectId
            // A project thread is never really empty — it already carries the
            // project's brief, its files and its audience. Saying so beats a
            // single grey line in the middle of a blank page.
            ? <ProjectThreadPrimer projectId={projectId} />
            : <p>{t('chat.emptyConversation') || 'Start a conversation by typing a message below'}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-messages">
      {validMessages.map((message) => (
        // render_key stays stable across the optimistic-message lifecycle
        // (placeholder → streaming → ai_complete), so the bubble keeps
        // the same React identity even when message_id flips from
        // `temp-ai-xxx` to the persisted UUID. Without this the wrapper
        // remounts on `ai_complete`, which is the visible flash/jump.
        // Persisted messages don't have render_key — message_id is fine
        // for them since it never changes after load.
        message.compaction ? (
          // The cut where the context was compacted — everything above it is
          // folded into the summary. A later round folds the marker itself,
          // so only the live one is drawn; the folded messages above it stay
          // readable. Collapsed by default, live or reloaded.
          message.compaction.superseded ? null : (
            <div key={message.message_id}>
              <CompactionMarker
                summary={message.content}
                summarizedCount={message.compaction.summarized_count}
              />
            </div>
          )
        ) : (
        <div key={message.render_key ?? message.message_id}>
          {/* Model switch indicator - shows above user message that triggered the model switch */}
          {modelSwitches[message.message_id] && (
            <div className="model-switch-indicator">
              <div className="model-switch-line" />
              <span className="model-switch-text">
                {t('chat.modelSwitched', {
                  from: modelSwitches[message.message_id].from,
                  to: modelSwitches[message.message_id].to
                }) || `Switched from ${modelSwitches[message.message_id].from} to ${modelSwitches[message.message_id].to}`}
              </span>
              <div className="model-switch-line" />
            </div>
          )}
          {/* Mode switch indicator — shows above the assistant message that uses the new mode */}
          {modeSwitches[message.message_id] && (
            <div className="mode-switch-indicator">
              <div className="mode-switch-line" />
              <span className="mode-switch-text">
                {t('chat.modeSwitched', {
                  from: modeSwitches[message.message_id].from,
                  to: modeSwitches[message.message_id].to
                }) || `Mode: ${modeSwitches[message.message_id].from} → ${modeSwitches[message.message_id].to}`}
              </span>
              <div className="mode-switch-line" />
            </div>
          )}
          <MessageItem
            message={message}
            onEditMessage={onEditMessage}
            onBranch={onBranch}
            onBranchToAgent={onBranchToAgent}
            onHandoffTeleport={onHandoffTeleport}
            onHandoffSplit={onHandoffSplit}
            onScrollToQuote={onScrollToQuote}
            onArtifactOpen={onArtifactOpen}
            onContinue={onContinue && !streaming && message.message_id === continuableMessageId
              ? () => onContinue(message) : undefined}
            isLastUserMessage={message.message_id === lastUserMessageId}
            isBranchPoint={message.message_id === branchPointMessageId}
            activeSkills={message.message_id === lastAssistantMessageId ? activeSkills : []}
            thinkingSteps={message.message_id === lastAssistantMessageId ? thinkingSteps : undefined}
            thinkingPhase={message.message_id === lastAssistantMessageId ? thinkingPhase : undefined}
            thinkingFilename={message.message_id === lastAssistantMessageId ? thinkingFilename : undefined}
            thinkingText={message.message_id === lastAssistantMessageId ? thinkingText : undefined}
            thinkingSince={message.message_id === lastAssistantMessageId ? thinkingSince : undefined}
            revealThinking={revealThinking}
            showSkillLabels={showSkillLabels}
            isStreaming={message.message_id === lastAssistantMessageId && (streaming || activeSkills.length > 0)}
            conversationId={conversationId}
            conversationTitle={conversationTitle}
            availableSkills={availableSkills}
            existingFeedback={skillFeedbackMap[message.message_id]?.expected_skills}
            onFeedbackChange={onSkillFeedbackChange}
            existingRating={skillFeedbackMap[message.message_id]?.rating ?? null}
            existingRatingReasons={skillFeedbackMap[message.message_id]?.reasons ?? NO_REASONS}
            existingRatingDescription={skillFeedbackMap[message.message_id]?.feedback_description ?? null}
            onRatingChange={onRatingChange}
            botColor={botColor}
            botName={botName}
            botVariant={botVariant}
            showSenderIdentity={showSenderIdentity}
            personalSharedNotice={sharedNoticeAt.has(message.message_id)}
            branchDisabled={branchDisabled}
            branchDisabledReason={branchDisabledReason}
            readOnly={readOnly}
            feedbackMarker={feedbackByMessage?.[message.message_id] ?? null}
            onAttachmentOpen={onAttachmentOpen}
          />
        </div>
        )
      ))}
    </div>
  );
}
